import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type MergeQueueStatus = 'pending' | 'merged' | 'not_same' | 'related' | 'deferred' | 'superseded';

interface MergeSignal {
  signal: string;
  strength: 'strong' | 'weak' | 'caution' | 'blocking';
}

interface MergeCandidateContact {
  id: number;
  display_name: string;
  company: string | null;
  primary_phone: string | null;
  primary_email: string | null;
  contact_class: string | null;
}

interface MergeCandidate {
  id: string;
  status: MergeQueueStatus;
  confidence: number;
  confidence_algo_version: string;
  signals: MergeSignal[];
  proposed_by: string;
  created_at: string;
  updated_at: string;
  candidates: [MergeCandidateContact, MergeCandidateContact];
}

interface MergeQueueResponse {
  candidates: MergeCandidate[];
  total: number;
  limit: number;
  offset: number;
  status: MergeQueueStatus;
  summary: Partial<Record<MergeQueueStatus, number>>;
  design_doc: string;
  note: string;
}

interface MergeHistoryItem {
  id: string;
  surviving_contact: { id: number; display_name: string };
  merged_contacts: Array<{ id: number; display_name: string }>;
  tier: string;
  confidence: number | null;
  reviewer_email: string | null;
  reviewer_decision_id: number | null;
  reason: string | null;
  reversed_at: string | null;
  reversed_by: string | null;
  reversed_reason: string | null;
  can_unmerge: boolean;
  created_at: string;
}

interface MergeHistoryResponse {
  merges: MergeHistoryItem[];
  total: number;
  limit: number;
  offset: number;
  include_reversed: boolean;
}

interface MergePreviewContact extends MergeCandidateContact {
  display_name_norm: string | null;
  company_norm: string | null;
  is_active: boolean;
  merged_into_id: number | null;
  created_source: string | null;
  last_updated_source: string | null;
}

interface MergePreviewResponse {
  queue: {
    id: string;
    status: MergeQueueStatus;
    confidence: number;
    signals: MergeSignal[];
  };
  contacts: MergePreviewContact[];
  evidence: {
    sources: unknown[];
    phones: unknown[];
    emails: unknown[];
    addresses: unknown[];
    active_antimerges: unknown[];
  };
  recommendation: {
    survivor_contact_id: number;
    merged_contact_id: number | null;
    rationale: string;
  };
  proposed_changes: {
    read_only: boolean;
    contacts_update: Array<{
      contact_id: number;
      set: { is_active: boolean; merged_into_id: number };
    }>;
    contact_merges_insert: unknown | null;
    child_rows_to_reassign: Record<string, number>;
    blocked_by_active_antimerge: boolean;
    note: string;
  };
}

type State =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: MergeQueueResponse }
  | { kind: 'error'; message: string };

type PreviewState =
  | { kind: 'idle' }
  | { kind: 'loading'; candidateId: string }
  | { kind: 'loaded'; candidateId: string; data: MergePreviewResponse }
  | { kind: 'error'; candidateId: string; message: string };

type HistoryState =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: MergeHistoryResponse }
  | { kind: 'error'; message: string };

const PAGE_SIZE = 25;
const STATUSES: Array<{ value: MergeQueueStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'merged', label: 'Merged' },
  { value: 'related', label: 'Related' },
  { value: 'not_same', label: 'Not same' },
  { value: 'superseded', label: 'Superseded' },
];

function formatSignal(signal: string): string {
  return signal.replaceAll('_', ' ');
}

function confidenceClass(confidence: number): string {
  if (confidence >= 0.85) return 'bg-emerald-50 text-emerald-800 ring-emerald-200';
  if (confidence >= 0.75) return 'bg-blue-50 text-blue-800 ring-blue-200';
  return 'bg-amber-50 text-amber-900 ring-amber-200';
}

function contactLine(contact: MergeCandidateContact): string {
  return [contact.company, contact.primary_email, contact.primary_phone].filter(Boolean).join(' · ');
}

export function MergesPlaceholder(): JSX.Element {
  const [status, setStatus] = useState<MergeQueueStatus>('pending');
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [actingId, setActingId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [previewState, setPreviewState] = useState<PreviewState>({ kind: 'idle' });
  const [historyState, setHistoryState] = useState<HistoryState>({ kind: 'loading' });
  const [includeReversed, setIncludeReversed] = useState(false);

  useEffect(() => {
    setOffset(0);
  }, [status]);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async (): Promise<void> => {
      try {
        const params = new URLSearchParams({
          status,
          limit: String(PAGE_SIZE),
          offset: String(offset),
        });
        const res = await fetch(`/api/merge-queue?${params.toString()}`, { credentials: 'include' });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `Request failed: ${res.status}`);
        }
        const data = (await res.json()) as MergeQueueResponse;
        if (!cancelled) setState({ kind: 'loaded', data });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, offset, refreshKey]);

  useEffect(() => {
    let cancelled = false;
    setHistoryState({ kind: 'loading' });
    void (async (): Promise<void> => {
      try {
        const params = new URLSearchParams({
          limit: '10',
          include_reversed: String(includeReversed),
        });
        const res = await fetch(`/api/merge-queue/history?${params.toString()}`, { credentials: 'include' });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `History failed: ${res.status}`);
        }
        const data = (await res.json()) as MergeHistoryResponse;
        if (!cancelled) setHistoryState({ kind: 'loaded', data });
      } catch (err) {
        if (!cancelled) {
          setHistoryState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [includeReversed, refreshKey]);

  async function decide(candidate: MergeCandidate, action: 'defer' | 'not-same' | 'related'): Promise<void> {
    let body: Record<string, string> = {};
    if (action === 'not-same') {
      const reason = window.prompt('Reason these contacts are not the same person?')?.trim();
      if (!reason) return;
      body = { reason };
    } else if (action === 'related') {
      const reason = window.prompt('Relationship note?')?.trim() || 'Related contacts sharing a customer context';
      body = { reason, relationship_type: 'household_member' };
    } else {
      body = { reason: 'Deferred from merge review surface' };
    }

    setActingId(candidate.id);
    try {
      const res = await fetch(`/api/merge-queue/${candidate.id}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const response = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(response.error ?? `Decision failed: ${res.status}`);
      }
      setRefreshKey((key) => key + 1);
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setActingId(null);
    }
  }

  async function loadPreview(candidate: MergeCandidate): Promise<void> {
    if (previewState.kind === 'loaded' && previewState.candidateId === candidate.id) {
      setPreviewState({ kind: 'idle' });
      return;
    }
    setPreviewState({ kind: 'loading', candidateId: candidate.id });
    try {
      const res = await fetch(`/api/merge-queue/${candidate.id}/preview`, { credentials: 'include' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Preview failed: ${res.status}`);
      }
      const data = (await res.json()) as MergePreviewResponse;
      setPreviewState({ kind: 'loaded', candidateId: candidate.id, data });
    } catch (err) {
      setPreviewState({
        kind: 'error',
        candidateId: candidate.id,
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }

  const data = state.kind === 'loaded' ? state.data : null;
  const total = data?.total ?? 0;
  const candidates = data?.candidates ?? [];
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="max-w-full space-y-4 overflow-x-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">Contact merge queue</h1>
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
              Identity governance
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
            Review duplicate-customer candidates with preview-only merge impact, evidence, history, and reversible
            decisions. Use this as an identity review surface, not a casual contact-edit shortcut.
          </p>
        </div>
      </div>

      <section className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as MergeQueueStatus)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
          >
            {STATUSES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          {STATUSES.map((item) => (
            <span key={item.value} className="text-xs text-slate-500">
              {item.label}: <span className="font-medium text-slate-800">{data?.summary[item.value] ?? 0}</span>
            </span>
          ))}
        </div>
      </section>

      <MergeHistoryPanel
        state={historyState}
        includeReversed={includeReversed}
        onToggleReversed={setIncludeReversed}
      />

      {state.kind === 'error' ? (
        <section className="rounded-xl bg-rose-50 p-4 ring-1 ring-rose-200">
          <p className="text-sm font-medium text-rose-900">Failed to load merge queue</p>
          <p className="mt-1 text-sm text-rose-800">{state.message}</p>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        {state.kind === 'loading' ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : candidates.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-700">No {status.replace('_', ' ')} merge candidates.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {candidates.map((candidate) => (
              <article key={candidate.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-semibold ring-1 ${confidenceClass(candidate.confidence)}`}>
                      {Math.round(candidate.confidence * 100)}%
                    </span>
                    <span className="text-xs text-slate-500">
                      {candidate.confidence_algo_version} · {candidate.proposed_by}
                    </span>
                  </div>
                  <span className="text-xs text-slate-500" title={candidate.created_at}>
                    queued {new Date(candidate.created_at).toLocaleString()}
                  </span>
                </div>

                <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
                  <ContactCard contact={candidate.candidates[0]} />
                  <div className="flex items-center justify-center text-xs font-semibold uppercase tracking-wide text-slate-400">
                    match
                  </div>
                  <ContactCard contact={candidate.candidates[1]} />
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {candidate.signals.map((signal) => (
                    <span
                      key={`${candidate.id}-${signal.signal}`}
                      className={`inline-flex items-center rounded-md px-2 py-1 text-xs ring-1 ${
                        signal.strength === 'strong'
                          ? 'bg-slate-900 text-white ring-slate-900'
                          : signal.strength === 'caution' || signal.strength === 'blocking'
                            ? 'bg-amber-50 text-amber-800 ring-amber-200'
                          : 'bg-slate-50 text-slate-700 ring-slate-200'
                      }`}
                    >
                      {formatSignal(signal.signal)}
                    </span>
                  ))}
                </div>

                {candidate.status === 'pending' ? (
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      disabled={previewState.kind === 'loading' && previewState.candidateId === candidate.id}
                      onClick={() => void loadPreview(candidate)}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      disabled={actingId === candidate.id}
                      onClick={() => void decide(candidate, 'defer')}
                      className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Defer
                    </button>
                    <button
                      type="button"
                      disabled={actingId === candidate.id}
                      onClick={() => void decide(candidate, 'related')}
                      className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Related
                    </button>
                    <button
                      type="button"
                      disabled={actingId === candidate.id}
                      onClick={() => void decide(candidate, 'not-same')}
                      className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-800 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Not same
                    </button>
                  </div>
                ) : null}

                {previewState.kind === 'loading' && previewState.candidateId === candidate.id ? (
                  <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-500 ring-1 ring-slate-200">
                    Loading preview…
                  </div>
                ) : null}

                {previewState.kind === 'error' && previewState.candidateId === candidate.id ? (
                  <div className="mt-4 rounded-lg bg-rose-50 p-4 text-sm text-rose-800 ring-1 ring-rose-200">
                    {previewState.message}
                  </div>
                ) : null}

                {previewState.kind === 'loaded' && previewState.candidateId === candidate.id ? (
                  <MergePreviewPanel preview={previewState.data} />
                ) : null}
              </article>
            ))}
          </div>
        )}
      </section>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <p>{total === 0 ? '0 results' : `${pageStart}-${pageEnd} of ${total}`}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
          >
            Next
          </button>
        </div>
      </div>

      {data?.note ? (
        <section className="rounded-xl bg-slate-100 p-4 text-xs text-slate-600 ring-1 ring-slate-200">
          {data.note}
        </section>
      ) : null}
    </div>
  );
}

function MergeHistoryPanel({
  state,
  includeReversed,
  onToggleReversed,
}: {
  state: HistoryState;
  includeReversed: boolean;
  onToggleReversed: (value: boolean) => void;
}): JSX.Element {
  const merges = state.kind === 'loaded' ? state.data.merges : [];
  return (
    <section className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Recent merge history</h2>
          <p className="mt-1 text-xs text-slate-500">
            Read-only audit trail. Merge execution and undo are disabled in W6-H.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={includeReversed}
            onChange={(event) => onToggleReversed(event.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
          />
          Include reversed
        </label>
      </div>

      {state.kind === 'loading' ? (
        <p className="mt-4 text-sm text-slate-500">Loading history…</p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800 ring-1 ring-rose-200">
          {state.message}
        </div>
      ) : null}

      {state.kind === 'loaded' && merges.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">No merge history yet.</p>
      ) : null}

      {merges.length > 0 ? (
        <div className="mt-4 divide-y divide-slate-100">
          {merges.map((item) => {
            const mergedNames = item.merged_contacts.map((contact) => `${contact.display_name} #${contact.id}`).join(', ');
            return (
              <div key={item.id} className="py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {mergedNames} → {item.surviving_contact.display_name} #{item.surviving_contact.id}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    #{item.id} · {item.tier} · {item.confidence === null ? 'manual' : `${Math.round(item.confidence * 100)}%`} · {new Date(item.created_at).toLocaleString()}
                    {item.reviewer_email ? ` · ${item.reviewer_email}` : ''}
                  </p>
                  {item.reason ? <p className="mt-1 text-xs text-slate-500">{item.reason}</p> : null}
                  {item.reversed_at ? (
                    <p className="mt-1 text-xs font-medium text-amber-800">
                      Reversed {new Date(item.reversed_at).toLocaleString()}
                      {item.reversed_by ? ` by ${item.reversed_by}` : ''}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function MergePreviewPanel({
  preview,
}: {
  preview: MergePreviewResponse;
}): JSX.Element {
  const survivor = preview.contacts.find((contact) => contact.id === preview.recommendation.survivor_contact_id);
  const merged = preview.contacts.find((contact) => contact.id === preview.recommendation.merged_contact_id);
  const counts = preview.proposed_changes.child_rows_to_reassign;
  const countLine = [
    `${counts.contact_sources ?? 0} sources`,
    `${counts.contact_phones ?? 0} phones`,
    `${counts.contact_emails ?? 0} emails`,
    `${counts.contact_addresses ?? 0} addresses`,
  ].join(' · ');

  return (
    <div className="mt-4 rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">Merge preview</p>
          <p className="mt-1 text-xs text-slate-600">{preview.recommendation.rationale}</p>
        </div>
        <span className="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
          Read-only
        </span>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase text-slate-500">Survivor</p>
          <p className="mt-1 text-sm font-medium text-slate-900">
            {survivor ? `${survivor.display_name} #${survivor.id}` : `#${preview.recommendation.survivor_contact_id}`}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-slate-500">Would soft-merge</p>
          <p className="mt-1 text-sm font-medium text-slate-900">
            {merged ? `${merged.display_name} #${merged.id}` : preview.recommendation.merged_contact_id ?? 'n/a'}
          </p>
        </div>
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <PreviewMetric label="Evidence rows" value={countLine} />
        <PreviewMetric label="Source records" value={String(preview.evidence.sources.length)} />
        <PreviewMetric label="Active anti-merges" value={String(preview.evidence.active_antimerges.length)} />
      </div>

      {preview.proposed_changes.blocked_by_active_antimerge ? (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 ring-1 ring-amber-200">
          Active anti-merge exists; approval would be blocked.
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">{preview.proposed_changes.note}</p>
        <span className="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
          Merge execution disabled
        </span>
      </div>
    </div>
  );
}

function PreviewMetric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md bg-white p-3 ring-1 ring-slate-200">
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 text-sm text-slate-900">{value}</p>
    </div>
  );
}

function ContactCard({ contact }: { contact: MergeCandidateContact }): JSX.Element {
  const details = contactLine(contact);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-start justify-between gap-3">
        <Link to={`/contacts/${contact.id}`} className="font-medium text-slate-900 hover:underline">
          {contact.display_name}
        </Link>
        <span className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-xs text-slate-500 ring-1 ring-slate-200">
          #{contact.id}
        </span>
      </div>
      {details.length > 0 ? (
        <p className="mt-1 text-sm text-slate-600">{details}</p>
      ) : (
        <p className="mt-1 text-sm text-slate-400">No email, phone, or company</p>
      )}
      {contact.contact_class ? (
        <p className="mt-2 text-xs text-slate-500">{contact.contact_class}</p>
      ) : null}
    </div>
  );
}
